import React, { useState } from "react";
import TapCard from "./TapCard";
import AddressAutocomplete from "../AddressAutocomplete";

const AU_STATES = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"];

const BUDGET_RANGES = [
  "Under $500",
  "$500–$1,500",
  "$1,500–$5,000",
  "$5,000–$15,000",
  "$15,000+",
  "Prefer not to say",
];

const WHEN_NEEDED = [
  { emoji: "🚨", label: "ASAP" },
  { emoji: "📅", label: "Within 2 weeks" },
  { emoji: "🗓️", label: "Within a month" },
  { emoji: "⏳", label: "Just getting quotes" },
  { emoji: "🌀", label: "Whenever" },
];

const PROPERTY_TYPES = [
  { emoji: "🏠", label: "House" },
  { emoji: "🏢", label: "Unit" },
  { emoji: "🏬", label: "Commercial" },
  { emoji: "🏗️", label: "Under construction" },
];

const inputStyle = {
  width: "100%",
  fontSize: "1rem",
  padding: "16px 18px",
  minHeight: "56px",
  borderRadius: "12px",
  border: "2px solid var(--border, #e5e7eb)",
  boxSizing: "border-box",
  outline: "none",
};

const errorInputStyle = {
  ...inputStyle,
  border: "2px solid #ef4444",
};

export default function StepContactDetails({ form, update, onNext, errors }) {
  const [fieldErrors, setFieldErrors] = useState({});

  function handleNext() {
    const errs = {};
    if (!form.fullName || !form.fullName.trim()) errs.fullName = "Please enter your name";
    // Bug 3 fix: phone OR email is sufficient — only block if both are missing
    if ((!form.phone || !form.phone.trim()) && (!form.email || !form.email.trim())) {
      errs.phone = "Please enter your mobile number or email";
    }
    // Suburb is the minimum location info we need — was previously enforced
    // in the deleted StepLocation, now lives here.
    if (!form.suburb || !form.suburb.trim()) {
      errs.suburb = "Please enter the job suburb";
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    onNext();
  }

  function clearError(field) {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  return (
    <div>
      {/* Name */}
      <div style={{ marginBottom: "16px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "0.95rem" }}>Your name</label>
        <input
          type="text"
          value={form.fullName}
          onChange={(e) => { update("fullName", e.target.value); clearError("fullName"); }}
          placeholder="Jane Smith"
          style={fieldErrors.fullName ? errorInputStyle : inputStyle}
        />
        {fieldErrors.fullName && <p style={{ color: "#ef4444", fontSize: "0.83rem", marginTop: "4px" }}>{fieldErrors.fullName}</p>}
      </div>

      {/* Mobile */}
      <div style={{ marginBottom: "16px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "0.95rem" }}>Mobile number <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(we need either mobile or email)</span></label>
        <input
          type="tel"
          inputMode="numeric"
          value={form.phone}
          onChange={(e) => { update("phone", e.target.value); clearError("phone"); }}
          placeholder="04xx xxx xxx"
          style={fieldErrors.phone ? errorInputStyle : inputStyle}
        />
        {fieldErrors.phone && <p style={{ color: "#ef4444", fontSize: "0.83rem", marginTop: "4px" }}>{fieldErrors.phone}</p>}
      </div>

      {/* Email */}
      <div style={{ marginBottom: "16px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "0.95rem" }}>
          Email address <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(we need either mobile or email)</span>
        </label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => { update("email", e.target.value); clearError("email"); }}
          placeholder="you@example.com"
          style={fieldErrors.email ? errorInputStyle : inputStyle}
        />
        {fieldErrors.email && <p style={{ color: "#ef4444", fontSize: "0.83rem", marginTop: "4px" }}>{fieldErrors.email}</p>}
      </div>

      {/* Street address — typeahead via OpenStreetMap; auto-fills suburb/state/postcode */}
      <div style={{ marginBottom: "16px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "0.95rem" }}>
          Street address <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(optional)</span>
        </label>
        <AddressAutocomplete
          value={form.address || ""}
          onChange={(v) => update("address", v)}
          onSelect={({ street, suburb, state, postcode }) => {
            // Auto-fan the parsed parts into the matching form fields. Only
            // overwrite empty fields so a customer who typed a suburb manually
            // doesn't lose their entry to a stale geocoder guess.
            update("address", street);
            if (suburb) update("suburb", suburb);
            if (state) update("state", state);
            if (postcode) update("postcode", postcode);
          }}
        />
        <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "var(--muted, #6b7280)" }}>
          Optional, but helps the tradie give a more accurate quote.
        </p>
      </div>

      {/* Suburb (required — the tradie needs to know where the job is) */}
      <div style={{ marginBottom: "16px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "0.95rem" }}>
          Suburb <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(required)</span>
        </label>
        <input
          type="text"
          value={form.suburb}
          onChange={(e) => { update("suburb", e.target.value); clearError("suburb"); }}
          placeholder="e.g. Bondi, Fitzroy, Newstead"
          style={fieldErrors.suburb ? errorInputStyle : inputStyle}
        />
        {fieldErrors.suburb && <p style={{ color: "#ef4444", fontSize: "0.83rem", marginTop: "4px" }}>{fieldErrors.suburb}</p>}
      </div>

      {/* Property type — moved from the deleted StepLocation step */}
      <div style={{ marginBottom: "16px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "0.95rem" }}>
          Property type <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(optional)</span>
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
          {PROPERTY_TYPES.map((p) => (
            <TapCard
              key={p.label}
              emoji={p.emoji}
              label={p.label}
              selected={form.propertyType === p.label}
              onSelect={() => update("propertyType", p.label)}
            />
          ))}
        </div>
      </div>

      {/* State — Bug 8 fix: tap cards instead of native select */}
      <div style={{ marginBottom: "16px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "0.95rem" }}>
          State <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(optional)</span>
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
          {AU_STATES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => update("state", form.state === s ? "" : s)}
              style={{
                background: form.state === s ? "rgba(255, 138, 0,0.07)" : "var(--surface, #fff)",
                border: `2px solid ${form.state === s ? "var(--primary)" : "var(--border, #e5e7eb)"}`,
                borderRadius: "12px",
                padding: "10px 6px",
                fontSize: "0.9rem",
                fontWeight: form.state === s ? 700 : 500,
                color: form.state === s ? "var(--primary)" : "var(--text, #1C1C1E)",
                cursor: "pointer",
                width: "100%",
                transition: "border-color 0.15s, background 0.15s",
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Postcode */}
      <div style={{ marginBottom: "28px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "0.95rem" }}>
          Postcode <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(optional)</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={form.postcode || ""}
          onChange={(e) => update("postcode", e.target.value)}
          placeholder="e.g. 2026"
          style={{ ...inputStyle, maxWidth: "160px" }}
        />
      </div>

      {/* Budget range */}
      <div style={{ marginBottom: "28px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "12px", fontSize: "0.95rem" }}>
          Budget range <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(optional)</span>
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
          {BUDGET_RANGES.map((b) => (
            <TapCard
              key={b}
              label={b}
              selected={form.budgetRange === b}
              onSelect={() => update("budgetRange", b)}
            />
          ))}
        </div>
      </div>

      {/* When needed */}
      <div style={{ marginBottom: "28px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "12px", fontSize: "0.95rem" }}>
          When do you need it done? <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(optional)</span>
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }} className="step-when-grid">
          {WHEN_NEEDED.map((w) => (
            <TapCard
              key={w.label}
              emoji={w.emoji}
              label={w.label}
              selected={form.whenNeeded === w.label}
              onSelect={() => update("whenNeeded", w.label)}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={handleNext}
        style={{
          background: "var(--primary)",
          color: "#fff",
          border: "none",
          borderRadius: "12px",
          padding: "18px 32px",
          fontSize: "1rem",
          fontWeight: 700,
          cursor: "pointer",
          width: "100%",
        }}
      >
        Next →
      </button>
    </div>
  );
}
